import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MonitorCog, ShoppingBag, Clock3, ArrowDownToLine, ArrowUpFromLine, FileText, History, Inbox, MonitorSmartphone, ScanSearch,
  PauseCircle, Scale, Tags, ReceiptText, Maximize, Keyboard, Printer, Hand, Banknote,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { fmtDate, round2, vals } from "@/lib/format";
import { currentShift, shiftReport } from "@/lib/shift";
import { getHeld, openCustomerDisplay, openDrawerManual, openShiftDialog, touchActive } from "@/lib/tpv";
import { useScale } from "@/lib/scale";
import { PageTitle, BigButton } from "@/components/ui-kit";
import ZReport from "@/components/tpv/ZReport";
import HelpDialog from "@/components/tpv/HelpDialog";
import TpvSettings from "@/components/tpv/TpvSettings";
import ScaleSettings from "@/components/scale/ScaleSettings";
import LabelsDialog from "@/components/inventory/LabelsDialog";
import WeighLabelDialog from "@/components/scale/WeighLabelDialog";

const Stat = ({ label, value, testid, tone = "" }) => (
  <div className={`min-w-0 rounded-2xl p-4 bg-white border border-border ${tone}`} data-testid={testid}>
    <div className="text-xs font-bold uppercase text-muted-foreground">{label}</div>
    <div className="text-2xl font-extrabold mt-1 truncate">{value}</div>
  </div>
);

const Tile = ({ icon: Icon, label, onClick, to, testid, kbd, disabled }) => {
  const cls = `group relative min-w-0 rounded-2xl bg-white border border-border p-4 min-h-[120px] flex flex-col items-center justify-center gap-2 text-center font-bold text-[#0B2A5B] hover:border-[#F0444B]/50 hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] transition-[transform,box-shadow,border-color] ${disabled ? "opacity-40 pointer-events-none" : ""}`;
  const inner = (
    <>
      <Icon className="w-10 h-10 group-hover:text-[#F0444B] transition-colors" strokeWidth={1.4} />
      <span className="leading-tight">{label}</span>
      {kbd && <kbd dir="ltr" className="absolute top-2 end-2 text-[10px] text-muted-foreground">{kbd}</kbd>}
    </>
  );
  return to ? <Link to={to} className={cls} data-testid={testid}>{inner}</Link> : <button type="button" onClick={onClick} className={cls} data-testid={testid}>{inner}</button>;
};

export default function Tpv() {
  const { t, view, money, lang, can } = useApp();
  const nav = useNavigate();
  const scale = useScale();
  const shift = currentShift(view);
  const rep = shift ? shiftReport(view, shift) : null;
  const [dlg, setDlg] = useState(null);
  const [z, setZ] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const todaySales = vals(view.sales).filter((s) => (s.created_at || "").slice(0, 10) === today);
  const products = vals(view.products).filter((p) => !p.deleted);
  const history = vals(view.shifts).filter((s) => s.status === "closed").sort((a, b) => b.closed_at.localeCompare(a.closed_at)).slice(0, 8);
  const held = getHeld().length;
  const full = () => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.())?.catch?.(() => {});

  return (
    <div className="space-y-6" data-testid="tpv-page">
      <PageTitle testid="tpv-page-title">
        <span className="flex items-center gap-2"><MonitorCog className="w-7 h-7" />{t("tpv_section")}</span>
        <span className="block text-sm font-normal text-muted-foreground mt-1">{t("tpv_section_desc")}</span>
      </PageTitle>
      <section className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Stat label={t("shift")} value={shift ? `${new Date(shift.opened_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })} · ${shift.opened_by}` : t("no_shift")} testid="tpv-stat-shift" tone={shift ? "" : "!border-[#F0444B]/50 !bg-[#F0444B]/5"} />
        <Stat label={t("expected_cash")} value={rep ? money(rep.expected) : "—"} testid="tpv-stat-expected" />
        <Stat label={t("today_sales")} value={money(round2(todaySales.reduce((s, x) => s + (x.total || 0), 0)))} testid="tpv-stat-today-total" />
        <Stat label={t("sales_count")} value={todaySales.length} testid="tpv-stat-today-count" />
        <Stat label={t("held_sales")} value={held} testid="tpv-stat-held" />
        <Stat label={t("scale")} value={scale.status === "connected" ? `${(scale.kg - scale.tare).toFixed(3)} kg` : t("scale_not_connected")} testid="tpv-stat-scale" />
      </section>
      <div className="flex flex-wrap gap-2 text-sm">
        <span className="rounded-full bg-secondary px-3 py-1.5 font-bold flex items-center gap-1.5" data-testid="tpv-touch-status"><Hand className="w-4 h-4" />{t("touch_mode")}: {touchActive() ? t("touch_on") : t("touch_off")}</span>
      </div>

      <section>
        <h2 className="font-heading text-xl mb-3">{t("tpv_operations")}</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Tile icon={MonitorCog} label={t("tm_touch")} to="/terminal" testid="tpv-tile-terminal" />
          <Tile icon={ShoppingBag} label={t("open_till")} to="/sell" testid="tpv-tile-sell" />
          <Tile icon={Clock3} label={shift ? t("close_shift") : t("open_shift")} onClick={() => openShiftDialog(shift ? "close" : "x")} testid="tpv-tile-shift" kbd="F6" />
          <Tile icon={ArrowDownToLine} label={t("cash_in")} onClick={() => openShiftDialog("in")} disabled={!shift} testid="tpv-tile-cash-in" />
          <Tile icon={ArrowUpFromLine} label={t("cash_out")} onClick={() => openShiftDialog("out")} disabled={!shift} testid="tpv-tile-cash-out" />
          <Tile icon={FileText} label={t("x_report")} onClick={() => openShiftDialog("x")} disabled={!shift} testid="tpv-tile-x" />
          <Tile icon={History} label={t("z_history")} onClick={() => setDlg("z")} testid="tpv-tile-z-history" />
          <Tile icon={Inbox} label={t("open_drawer")} onClick={openDrawerManual} testid="tpv-tile-drawer" kbd="F9" />
          <Tile icon={MonitorSmartphone} label={t("customer_display")} onClick={openCustomerDisplay} testid="tpv-tile-display" kbd="F10" />
          <Tile icon={ScanSearch} label={t("price_check")} onClick={() => nav("/price-check")} testid="tpv-tile-price-check" kbd="F12" />
          <Tile icon={PauseCircle} label={`${t("held_sales")} (${held})`} onClick={() => nav("/sell?recall=1")} testid="tpv-tile-held" kbd="⇧F5" />
          <Tile icon={Scale} label={t("weigh_label")} onClick={() => setDlg("weigh")} disabled={!can("inventory")} testid="tpv-tile-weigh-label" />
          <Tile icon={Tags} label={t("labels")} onClick={() => setDlg("labels")} disabled={!can("inventory")} testid="tpv-tile-labels" />
          <Tile icon={ReceiptText} label={t("sales_history")} to="/sales" testid="tpv-tile-sales" />
          <Tile icon={Banknote} label={t("nav_reports")} to="/reports" disabled={!can("reports")} testid="tpv-tile-reports" />
          <Tile icon={Printer} label={t("tpv_terminal")} onClick={() => document.getElementById("tpv-device")?.scrollIntoView({ behavior: "smooth" })} testid="tpv-tile-printer" />
          <Tile icon={Scale} label={t("scale_and_scanner")} onClick={() => document.getElementById("tpv-scale")?.scrollIntoView({ behavior: "smooth" })} testid="tpv-tile-scale" />
          <Tile icon={Keyboard} label={t("shortcuts")} onClick={() => setDlg("help")} testid="tpv-tile-shortcuts" kbd="F1" />
          <Tile icon={Maximize} label={t("fullscreen")} onClick={full} testid="tpv-tile-fullscreen" />
        </div>
      </section>

      <div className="grid lg:grid-cols-2 gap-4 items-start">
        <div id="tpv-device" className="scroll-mt-20 min-w-0"><TpvSettings /></div>
        <div id="tpv-scale" className="scroll-mt-20 min-w-0"><ScaleSettings /></div>
      </div>

      <Dialog open={dlg === "z"} onOpenChange={(o) => { if (!o) { setDlg(null); setZ(null); } }}>
        <DialogContent className="dlg-full" data-testid="tpv-z-history-dialog">
          <div className="max-w-md mx-auto w-full space-y-3">
          <DialogTitle className="font-heading text-2xl">{t("z_history")}</DialogTitle>
          {z ? (
            <div className="space-y-3">
              <ZReport shift={z} />
              <BigButton variant="soft" onClick={() => setZ(null)} className="w-full no-print" data-testid="tpv-z-back">{t("back")}</BigButton>
            </div>
          ) : history.length === 0 ? <p className="text-muted-foreground" data-testid="tpv-z-empty">{t("no_closed_shifts")}</p> : history.map((h) => (
            <button key={h.id} type="button" onClick={() => setZ(h)} className="w-full text-start rounded-xl bg-secondary px-3 py-3 text-sm" data-testid="tpv-z-row">
              <div className="font-bold">{fmtDate(h.opened_at, lang)} → {fmtDate(h.closed_at, lang)}</div>
              <div className="text-muted-foreground">{h.closed_by} · {t("counted_cash")} {money(h.counted)}</div>
            </button>
          ))}
          </div>
        </DialogContent>
      </Dialog>
      <HelpDialog open={dlg === "help"} onClose={() => setDlg(null)} />
      <LabelsDialog open={dlg === "labels"} onClose={() => setDlg(null)} products={products} />
      <WeighLabelDialog open={dlg === "weigh"} onClose={() => setDlg(null)} products={products} />
    </div>
  );
}
